import * as React from 'react';
import { useCustomEventEffect } from '../../hooks';
import { sanitizeContent } from '../../thumbtack';
import { HiddenInputs } from './shared/HiddenInputs';
import {
  IAnswerMatchParams,
  IQuestionAnswerUpdateEventDetails,
  IQuestionDetails,
  matchQuestionConditions,
} from './shared/utilities';

interface IProps {
  initialInputValue?: string;
  name: string;
  id: string;
  questionDetails: IQuestionDetails;
  conditionalQuestionParentQuestionId: string;
  conditionalQuestionConditions: { operator: string; value: string }[];
  formResourceType: 'application' | 'job_seeker';
}

export default function ShortText(props: IProps) {
  const {
    initialInputValue,
    name,
    id,
    questionDetails,
    conditionalQuestionConditions,
    conditionalQuestionParentQuestionId,
    formResourceType,
  } = props;
  const { index, questionId, questionType, title, additionalInformation, required } = questionDetails;

  const condition = conditionalQuestionConditions && conditionalQuestionConditions[0];
  const [visible, setVisible] = React.useState<boolean>(conditionalQuestionParentQuestionId === null);

  const [inputValue, setInputValue] = React.useState<string>(initialInputValue || '');

  function handleInputChange(event: React.FormEvent<HTMLInputElement>) {
    setInputValue(event.currentTarget.value);
    announceAnswerUpdate({ questionType: 'short_text', value: event.currentTarget.value });
  }

  /**
   * Sender - announce to possible conditional question answers when the value of this rendered component changes
   */
  const { dispatch: announceAnswerUpdate } = useCustomEventEffect(`question:${questionId}:answer:update`);

  React.useEffect(() => {
    setTimeout(() => {
      announceAnswerUpdate({ questionType: 'short_text', value: inputValue });
    }, 250);
  }, []);

  /**
   * Receiver - receive announcements from possible parent conditional questions about their changing value(s) so
   * we know whether to hide or show this answer component
   */
  useCustomEventEffect(
    `question:${conditionalQuestionParentQuestionId}:answer:update`,
    ({ detail }: CustomEvent<IQuestionAnswerUpdateEventDetails>) => {
      const matchParams: IAnswerMatchParams = {
        questionType: detail.questionType,
        value: detail.value,
        parentQuestionCondition: condition,
        maxNumAnswerOptions: detail.maxNumAnswerOptions,
      };
      // Only hide element if it's got a parent conditional question
      if (conditionalQuestionParentQuestionId) {
        setVisible(matchQuestionConditions(matchParams));
      }
    },
  );

  if (!visible) return null;

  return (
    <div className="pad-v-3">
      <label
        className="external-form__label"
        htmlFor={`${formResourceType}_form_${formResourceType}_answers_attributes_${index}_text_answer`}
      >
        <span className={`external-form__label--title ${required ? 'external-form__label--required' : ''}`}>
          {title}
        </span>
        {additionalInformation && <span dangerouslySetInnerHTML={{ __html: sanitizeContent(additionalInformation) }} />}
      </label>
      <div className="frow frow--gutters frow--direction-column frow--row-tn frow--items-tn-center frow--nowrap">
        <div className="col-flex-grow-1">
          <div style={{ position: 'relative' }}>
            <input
              id={id}
              type="text"
              name={name}
              value={inputValue}
              onChange={handleInputChange}
              required={required}
            />
          </div>
        </div>
      </div>
      <HiddenInputs
        formResourceType={formResourceType}
        index={index}
        additionalInformation={additionalInformation}
        title={title}
        questionId={questionId}
        questionType={questionType}
      />
    </div>
  );
}
